// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN SYSTEM BOARD (Erez 2026-07-19) — refactored to the LOCKED board-container
// standard (Jarvis, 2026-07-19). Holds OUR Section Library: our reusable
// content-page section patterns, grouped by category. Each CATEGORY is one
// <SectionBox> (big black label); each section screenshot is a WHITE card in an
// internal grid; ALL SectionBoxes live inside ONE <SectionGrid> in ONE React Flow
// node, so the board reads as one wide rectangle. Rendered through <BoardCanvas>.
// ─────────────────────────────────────────────────────────────────────────────

const SEC_HASH = "https://3e59308f.tailormind-site.pages.dev";
const FONT = "Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const CARD = "#FFFFFF";
const CARD_BORDER = "#E4E0DA";
const INK = "#1C1917";
const INK2 = "#57534E";

const IMG = (bust: string) => (id: string) => `${SEC_HASH}/board/sections/${id}.jpg${bust}`;

// ── Content: OUR 38 sections grouped by CATEGORY (all preserved) ─────────────
type Cat = { title: string; note: string; cols: number; ids: string[] };

const CATEGORIES: Cat[] = [
  { title: "Heroes", note: "The two homepage hero patterns.", cols: 2, ids: ["centered-hero", "hero-with-preview"] },
  { title: "Discovery Heroes", note: "Service hero · Discovery & AI Audit — pick 1 of 4.", cols: 2, ids: ["hero-discovery-1", "hero-discovery-2", "hero-discovery-3", "hero-discovery-4"] },
  { title: "Build Heroes", note: "Service hero · Build & Implement — pick 1 of 4.", cols: 2, ids: ["hero-build-1", "hero-build-2", "hero-build-3", "hero-build-4"] },
  { title: "Run & Grow Heroes", note: "Service hero · Run & Grow — pick 1 of 4.", cols: 2, ids: ["hero-run-1", "hero-run-2", "hero-run-3", "hero-run-4"] },
  { title: "Enablement Heroes", note: "Service hero · Enablement — pick 1 of 4.", cols: 2, ids: ["hero-enablement-1", "hero-enablement-2", "hero-enablement-3", "hero-enablement-4"] },
  { title: "Feature Sections", note: "Our reusable feature-section patterns.", cols: 4, ids: ["icon-row", "bento-feature", "alt-feature", "three-up-cards", "checklist-split", "big-mock-showcase", "feature-accordion", "tabs-showcase", "feature-split-right", "feature-split-left", "feature-2x2", "feature-rows", "bento-asymmetric", "pillars-row"] },
  { title: "Process", note: "How-it-works / process patterns.", cols: 3, ids: ["numbered-steps", "process-timeline", "steps-cards"] },
  { title: "Proof", note: "Proof & comparison patterns.", cols: 3, ids: ["testimonial-video", "comparison-table", "before-after", "logo-marquee", "testimonial-trio", "comparison-two-col"] },
  { title: "Stats", note: "Stats & metrics patterns.", cols: 2, ids: ["stat-roi", "metric-callout", "mini-stats-row", "stat-cards"] },
  { title: "Editorial", note: "Editorial & content patterns.", cols: 2, ids: ["two-col-prose", "quote-band", "callout-band", "resource-cards"] },
  { title: "Pricing · CTA · FAQ", note: "Pricing, CTA and FAQ patterns.", cols: 3, ids: ["pricing-tiers", "cta-band", "cta-split", "faq-accordion", "integrations-grid"] },
];

const IMG_W = 1080;

// ── White section-pattern card: the screenshot + its section id ──────────────
function PatternCard({ id, src }: { id: string; src: string }) {
  return (
    <div
      style={{
        width: IMG_W,
        background: CARD,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 26,
        boxShadow: "0 20px 50px rgba(28,25,23,0.10)",
        overflow: "hidden",
      }}
    >
      <div style={{ fontSize: 0, lineHeight: 0, background: "#FAF7F2" }}>
        <img src={src} alt={id} draggable={false} style={{ width: "100%", height: "auto", display: "block" }} />
      </div>
      <div style={{ padding: "26px 34px", borderTop: `1px solid ${CARD_BORDER}`, fontSize: 34, fontWeight: 600, letterSpacing: "-0.01em", color: INK, fontFamily: MONO }}>
        {id}
      </div>
    </div>
  );
}

// full-width white caption card at the top of a category grid (the guidance line)
function NoteCard({ text }: { text: string }) {
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        background: CARD,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 26,
        boxShadow: "0 20px 50px rgba(28,25,23,0.10)",
        padding: "34px 40px",
        fontSize: 42,
        fontWeight: 500,
        lineHeight: 1.35,
        color: INK2,
      }}
    >
      {text}
    </div>
  );
}

function CategoryBox({ cat, img }: { cat: Cat; img: (id: string) => string }) {
  return (
    <SectionBox title={cat.title} layout="custom">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cat.cols}, ${IMG_W}px)`, gap: 64, alignItems: "start" }}>
        <NoteCard text={cat.note} />
        {cat.ids.map((id) => (
          <PatternCard key={id} id={id} src={img(id)} />
        ))}
      </div>
    </SectionBox>
  );
}

// ── The ONE board node: every SectionBox inside one SectionGrid ──────────────
function BoardNode({ data }: { data: { bust: string } }) {
  const img = IMG(data.bust);
  return (
    <div style={{ fontFamily: FONT }}>
      <SectionGrid>
        <SectionBox title="Section Library" layout="custom">
          <div style={{ width: IMG_W * 2, background: CARD, border: `1px solid ${CARD_BORDER}`, borderRadius: 26, boxShadow: "0 20px 50px rgba(28,25,23,0.10)", padding: "44px 48px" }}>
            <div style={{ fontSize: 46, fontWeight: 500, lineHeight: 1.35, color: INK2 }}>
              Our reusable content-page section patterns, in our design, grouped by category. Pick the ones for each real page.
            </div>
          </div>
        </SectionBox>
        {CATEGORIES.map((cat) => (
          <CategoryBox key={cat.title} cat={cat} img={img} />
        ))}
      </SectionGrid>
    </div>
  );
}

const nodeTypes: NodeTypes = { board: BoardNode };

export function DesignSystemBoardPage() {
  const nodes = useMemo<Node[]>(() => {
    const bust = "?v=2026-07-21t";
    return [
      { id: "board", type: "board", position: { x: 0, y: 0 }, data: { bust }, draggable: false, selectable: false },
    ];
  }, []);

  return <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} />;
}
